import { useQueryClient } from '@tanstack/react-query';
import { getFindProductGridKey } from '../../../../../services/modules/product-grid/product-grid.keys';
import { FindProductGridOutput } from '../../../../../services/modules/product-grid/product-grid.types';
import { retrieveOrderProductFromUniqueIdentifier } from '../../../../../utils/getOrderProductUniqueIdentifier';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { isOfflineStatus } from '../../../../offline/domain/offlineOrder/offlineOrder.types';
import { useOrderContext } from '../../../contexts/Order.context';
import { useOrderCalculateContext } from '../../../contexts/OrderCalculate.context';
import {
  OrderProductsMassActionsSummaryTotals,
  UseOrderProductsMassActionsSummaryParams,
} from '../../components/OrderProductsMassActionsSummary/orderProductsMassActionsSummary.types';

const useOrderProductsMassActionsSummary = ({
  selectedItems,
  isAllSelected,
}: UseOrderProductsMassActionsSummaryParams): OrderProductsMassActionsSummaryTotals => {
  const { team } = useApplicationContext();
  const { order } = useOrderContext();
  const { summaryTotals } = useOrderCalculateContext();
  const queryClient = useQueryClient();

  if (isAllSelected) {
    return {
      totalPieces: summaryTotals?.calculate?.total?.pieces ?? 0,
      totalValue: summaryTotals?.calculate?.total?.total ?? 0,
    };
  }

  return Array.from(selectedItems).reduce<OrderProductsMassActionsSummaryTotals>(
    (accumulator, currentValue) => {
      const orderProduct = retrieveOrderProductFromUniqueIdentifier(currentValue);
      if (!orderProduct) {
        return accumulator;
      }

      const key = getFindProductGridKey({
        ...orderProduct,
        orderId: order?.id,
        isOfflineMode: isOfflineStatus(order?.status),
      });
      const data = queryClient.getQueryData<FindProductGridOutput | null>([team?.id, ...key]);

      if (!data) {
        return accumulator;
      }

      data.grid.forEach((grid) => {
        const quantity = grid.orderItemQuantity ?? 0;
        const price = grid.orderPromotionalPrice ?? grid.orderWholeSalePrice;
        accumulator.totalPieces += quantity;
        accumulator.totalValue += quantity * price;
      });

      return accumulator;
    },
    { totalPieces: 0, totalValue: 0 },
  );
};

export default useOrderProductsMassActionsSummary;
